/**
 * slipTemplate — the printable despatch slip, and nothing else.
 *
 * Split out of DispatchService because it is pure presentation: given rows
 * and a couple of lookups it returns a string. Living inside the service
 * meant importing the print form transitively pulled in the SharePoint
 * client and MSAL, so the one part of dispatch with no I/O at all could not
 * be rendered or measured without a browser and a signed-in user.
 *
 * The layout mirrors the physical form (Despatch_Slip_Reference.xlsx): OPC
 * masthead, 11 columns ending at VERIFIED, 25 numbered rows, signatories on
 * the rule. Row metrics are tuned so a FULL slip still prints on one A4
 * landscape page — verified by rendering, not by arithmetic.
 *
 * A slip with more than SLIP_MAX_ROWS WIPs (a big batch to one sewer) is
 * still ONE DS number — the row cap describes the physical form, not the
 * dispatch. It continues onto additional printed pages, each a fresh copy of
 * the same form with its own 1-25 numbering (matching a blank page from the
 * same booklet) and a "(page N of M)" mark beside the DESPATCH NO. so it is
 * never mistaken for a second slip. Signatories/totals repeat on every page:
 * whichever page ends up in front of someone, it reads as complete on its
 * own.
 */
import type { ISlip } from "./DispatchService";

/** Rows on the physical form (Despatch_Slip_Reference: numbered 1-25), so a
 * slip never carries more WIPs than the paper has lines for. Owned here
 * because it describes the FORM; the service re-exports it for callers that
 * chunk by it. */
export const SLIP_MAX_ROWS = 25;

/**
 * A slip that carries repairs prints a SHORTER form — 17 lines, not 25 (Mikko,
 * 2026-10-20) — so the repair block under it, the signatures and all fit the
 * one A4 sheet instead of spilling its last rows onto a second. Two of the 17
 * stay empty after the last new-work line, then the REPAIR block follows.
 */
export const SLIP_REPAIR_FORM_ROWS = 17;
export const SLIP_REPAIR_GAP = 2;

/**
 * Column widths of the form, in % of the sheet (NO, WIP, CUTTER, STORE, SERIAL
 * ID, DUE DATE, DESIGN, COLOR, SIZE, QTY, VERIFIED). Both tables are
 * fixed-layout on these, and the repair table is the first ten scaled to the
 * space left of VERIFIED, so every repair column sits exactly under its new-work
 * column (Mikko, 2026-10-20: "follow the column format of Regular WIP").
 */
const COL_PCT = [4, 13, 10, 9, 11, 11, 9, 12, 6, 6, 9] as const;
const VERIFIED_PCT = COL_PCT[10];
const colgroup = (scaled: boolean): string => {
  const cols = scaled ? COL_PCT.slice(0, 10).map(w => (w / (100 - VERIFIED_PCT)) * 100) : COL_PCT;
  return `<colgroup>${cols.map(w => `<col style="width:${w.toFixed(3)}%">`).join("")}</colgroup>`;
};
/**
 * Lines (form + repair rows) one A4 sheet holds with the repair heading and the
 * signatures: 25 on the plain form, less the heading and its gap. A slip whose
 * new work + two empty lines + repairs fits inside it prints COMPACT — only
 * as many form lines as it needs, the repair block straight after — so it is
 * one sheet instead of a mostly-empty form with the signatures on a second
 * (Mikko, 2026-10-20: SOLEDAD, 3 new + 9 repair).
 */
export const SLIP_REPAIR_PAGE_BUDGET = 23;

/** What the form needs to know about one WIP. */
export interface ISlipWipDetail {
  cutter?: string; store?: string; serial?: string; due?: string;
  design?: string; color?: string; size?: string; qty?: number;
  /**
   * "RETURN REPAIR", or "RETURN REPAIR · ROUND 2" — set only for a bundle
   * QC sent back that is going out again. The floor and the subcontractor
   * both need this ON THE PAPER: a repair looks exactly like new work in
   * the bundle, and the only difference is what the slip says.
   *
   * Printed as a short marker in the WIP cell rather than a twelfth column,
   * because the form mirrors a physical reference (Despatch_Slip_Reference)
   * that has eleven and ends at VERIFIED. See buildSlipHtml.
   */
  repair?: string;
}

/** The marker that goes IN the WIP cell. Deliberately short: the row height
 * is tuned so 25 lines fit one A4 page, so the tag must not wrap the cell
 * onto a second line. "RETURN REPAIR · ROUND 2" is the tooltip's language;
 * on the paper, beside an 8-digit WIP, it has to be this. */
function repairTag(repair: string | undefined): string {
  if (!repair) return "";
  const round = repair.match(/ROUND\s*(\d+)/i);
  return round ? `REPAIR&#183;${round[1]}` : "REPAIR";
}

/**
 * A slip's set total, RE-DERIVED from the rows it is about to print.
 *
 * WHY NOT `slip.totalSets`, which is right there. That column is a snapshot
 * taken when the slip was issued, and every other figure on the paper is read
 * live: the QTY column comes from the WIP as it stands NOW. So editing a
 * bundle's quantity in iSyn_WIPs moved the line and left the header behind —
 * DS 4531 printed 1+5+5+2+5 down the page and TOTAL SETS: 14 at the top
 * (Mikko, 2026-09-16). A slip whose stated total disagrees with its own rows
 * is the thing a subcontractor notices at the window, and slipEdit already
 * said so about amendments: "Recomputed, never adjusted by a delta."
 *
 * THE INVARIANT IS THE COLUMN, not the stored number: this total is the sum
 * of exactly what the QTY column prints, so it can be checked on the paper
 * by adding it up. A bundle the caller cannot price prints a blank cell and
 * contributes nothing — which keeps the sum honest and leaves the gap
 * visible, rather than printing an authoritative-looking total over rows that
 * do not add up to it.
 */
export function slipLiveSets(
  slip: ISlip,
  wipDetail: (wip: string, slip: ISlip) => ISlipWipDetail,
): number {
  let n = 0;
  for (const w of slip.wipIds) {
    const q = wipDetail(w, slip).qty;
    if (typeof q === "number" && Number.isFinite(q) && q > 0) n += q;
  }
  return n;
}

/** Full name and ID for the CONTRACTOR lines (the Contractors Ref role). */
export interface ISlipContractor { name?: string; code?: string }

/** Digits out of a DS label ("DS 4290 · PREVIEW" -> 4290), or null. */
function dsSeq(dsNumber: string): number | null {
  const m = dsNumber.match(/\d+/);
  return m ? Number(m[0]) : null;
}

/**
 * The name the browser offers when saving the print as a PDF — the print
 * dialog takes it from the document title, so this IS the file name.
 *
 *   one slip   "DS 4290 - ARINGO"        — the number leads, so a folder of
 *                                          slips sorts in issue order
 *   many slips "DS 4288-4295"            — the range they cover; listing
 *                                          every sewer would run past what
 *                                          a file name can hold
 */
export function slipDocTitle(
  slips: ISlip[],
  contractorOf?: (sewer: string) => { name?: string; code?: string } | undefined
): string {
  if (slips.length === 0) return "Dispatch Slips";
  if (slips.length === 1) {
    const s = slips[0];
    const who = (contractorOf?.(s.sewer)?.name || s.sewer).trim();
    const ds = s.dsNumber.trim();
    // A row missing both is malformed, but the print must still save under
    // SOME name — a blank title makes the browser invent one.
    if (!ds) return who || "Dispatch Slip";
    return who ? `${ds} - ${who}` : ds;
  }
  const seqs = slips.map(s => dsSeq(s.dsNumber)).filter((n): n is number => n !== null);
  if (seqs.length === 0) return "Dispatch Slips";
  const lo = Math.min(...seqs), hi = Math.max(...seqs);
  return lo === hi ? `DS ${lo}` : `DS ${lo}-${hi}`;
}

/** Printable A4 slip HTML — from PERSISTED rows only (theme-exempt; mirrors
 * the physical form per the Despatch_Slip_Reference workbook: 11 columns
 * ending at VERIFIED, contractor's full name and ID from the roster rather
 * than the sewer key). Returns full document HTML. */
export function buildSlipHtml(
  slips: ISlip[],
  // The slip is passed as the second argument because `repair` is anchored
  // to the slip's OWN issue time, not to now — see repairRoundsAsOf. A
  // caller that does not need it simply declares one parameter.
  wipDetail: (wip: string, slip: ISlip) => ISlipWipDetail,
  contractorOf?: (sewer: string) => { name?: string; code?: string } | undefined
): string {
  const esc = (s: string): string => s.replace(/[&<>]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] as string));
  const pages = slips.flatMap(p => {
    // QC's returned repairs are NOT part of the 25-line form: they print in
    // their own block under it (Mikko, 2026-10-19: "the REPAIR would reflect
    // under / below the DS"), so new work keeps its lines and a repair is never
    // mistaken for it. Only WIPs that were repair work at the slip's own issue
    // time carry `repair`; every other WIP prints exactly as before.
    const detail = new Map(p.wipIds.map(w => [w, wipDetail(w, p)] as const));
    const repairIds = p.wipIds.filter(w => !!detail.get(w)?.repair);
    const mainIds = p.wipIds.filter(w => !detail.get(w)?.repair);
    // A DS with ONLY repairs (nothing new assigned to the sewer) has no new
    // work to keep apart from: the repairs take the numbered lines from 1, in
    // red, rather than hang under an empty form.
    const repairOnly = mainIds.length === 0 && repairIds.length > 0;
    const hasRepair = repairIds.length > 0;
    const compact = hasRepair && !repairOnly
      && mainIds.length + SLIP_REPAIR_GAP + repairIds.length <= SLIP_REPAIR_PAGE_BUDGET;
    const rowsPer = compact ? mainIds.length + SLIP_REPAIR_GAP : hasRepair ? SLIP_REPAIR_FORM_ROWS : SLIP_MAX_ROWS;
    const lineIds = repairOnly ? repairIds : mainIds;
    // The last page keeps two empty lines before the REPAIR block.
    const pageCount = Math.max(1, Math.ceil((lineIds.length + (hasRepair && !repairOnly ? SLIP_REPAIR_GAP : 0)) / rowsPer));
    const contractor = contractorOf?.(p.sewer);
    // Once per slip, not once per page: the total shown is the WHOLE slip's,
    // so every page reads as complete on its own (see the note below).
    const liveSets = slipLiveSets(p, wipDetail);
    // With repairs on the slip the header splits new work from repair
    // (Mikko, 2026-10-20: "Total Sets = 24 | 7 Repair", the repair in red).
    // Each side adds up exactly what its own QTY column prints.
    const sumQty = (ids: readonly string[]): number =>
      ids.reduce((n, w) => { const q = detail.get(w)?.qty; return n + (typeof q === "number" && Number.isFinite(q) && q > 0 ? q : 0); }, 0);
    const totalCell = hasRepair
      ? `${sumQty(mainIds)} | <span class="reptot">${sumQty(repairIds)} Repair</span>`
      : String(liveSets);
    const date = p.generatedAt ? new Date(p.generatedAt).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "";
    return Array.from({ length: pageCount }, (_, pageIdx) => {
      const offset = pageIdx * rowsPer;
      // Always the full 25 lines of the physical form on every page — a page
      // that shrinks to fit its contents stops being the same document the
      // floor knows, and leaves nowhere to hand-write a late addition.
      const rows: string[] = [];
      for (let i = 0; i < rowsPer; i++) {
        const w = lineIds[offset + i];
        if (w) {
          const d = detail.get(w)!;
          const tag = repairOnly ? ` <span class="rep">${repairTag(d.repair)}</span>` : "";
          rows.push(`<tr${repairOnly ? ' class="repr"' : ""}><td>${i + 1}</td><td>${esc(w)}${tag}</td><td>${esc(d.cutter ?? "")}</td><td>${esc(d.store ?? "")}</td><td>${esc(d.serial ?? "")}</td><td>${esc(d.due ?? "")}</td><td>${esc(d.design ?? "")}</td><td>${esc(d.color ?? "")}</td><td>${esc(d.size ?? "")}</td><td>${d.qty ?? ""}</td><td></td></tr>`);
        } else {
          rows.push(`<tr><td>${i + 1}</td>${"<td></td>".repeat(10)}</tr>`);
        }
      }
      // "(page N of M)" beside the DS number, but only when there IS more
      // than one page — the vast majority of slips print exactly as they
      // always have. Totals show the WHOLE slip's total on every page, not
      // just this page's rows, so any single page reads as complete on its
      // own rather than as a fragment someone has to add up.
      // The repair block closes the LAST page only, above the signatures.
      const repairRows = pageIdx === pageCount - 1 && !repairOnly ? repairIds.map((w, i) => {
        const d = detail.get(w)!;
        return `<tr><td>${i + 1}</td><td>${esc(w)} <span class="rep">${repairTag(d.repair)}</span></td><td>${esc(d.cutter ?? "")}</td><td>${esc(d.store ?? "")}</td><td>${esc(d.serial ?? "")}</td><td>${esc(d.due ?? "")}</td><td>${esc(d.design ?? "")}</td><td>${esc(d.color ?? "")}</td><td>${esc(d.size ?? "")}</td><td>${d.qty ?? ""}</td></tr>`;
      }).join("") : "";
      const repairBlock = repairRows
        ? `<div class="repl">REPAIR &#8212; RETURNED TO SUBCONTRACTOR</div><table class="items rept" style="width:${100 - VERIFIED_PCT}%">${colgroup(true)}<tbody>${repairRows}</tbody></table>`
        : "";
      const dsLabel = pageCount > 1 ? `${esc(p.dsNumber)} (page ${pageIdx + 1} of ${pageCount})` : esc(p.dsNumber);
      return `<div class="slip">
        <div class="brand">INTAL GARMENTS OPC</div>
        <div class="doc">Despatch Slip</div>
        <div class="gap"></div>
        <table class="hdr"><tr>
          <td class="hl">CONTRACTOR NAME:</td><td class="hv">${esc(contractor?.name || p.sewer)}</td><td class="sp"></td>
          <td class="hl">DESPATCH NO:</td><td class="hv">${dsLabel}</td><td class="sp"></td>
          <td class="hl">TOTAL SETS:</td><td class="hv">${totalCell}</td>
        </tr><tr>
          <td class="hl">CONTRACTOR ID NO.:</td><td class="hv">${esc(contractor?.code || p.sewerCode)}</td><td class="sp"></td>
          <td class="hl">DATE:</td><td class="hv">${esc(date)}</td><td class="sp"></td><td></td><td></td>
        </tr></table>
        <table class="items">${colgroup(false)}<thead><tr><th>NO.</th><th>WIP</th><th>CUTTER</th><th>STORE</th><th>SERIAL ID</th><th>DUE DATE</th><th>DESIGN</th><th>COLOR</th><th>SIZE</th><th>QTY</th><th>VERIFIED</th></tr></thead>
        <tbody>${rows.join("")}</tbody></table>
        ${repairBlock}
        <table class="foot"><tr>
          <td class="sig"><div class="nm">${esc(p.signatoryDispatchedBy) || "&nbsp;"}</div><div class="sl">DESPATCHED BY</div></td>
          <td class="sig"><div class="nm">${esc(p.signatoryPreparedBy) || "&nbsp;"}</div><div class="sl">PREPARED BY</div></td>
          <td class="sig"><div class="nm">&nbsp;</div><div class="sl">RECEIVED BY</div></td>
        </tr></table>
      </div>`;
    });
  }).join("");
  const docTitle = slipDocTitle(slips, contractorOf);
  return `<!DOCTYPE html><html><head><title>${esc(docTitle)}</title><style>
    @page { size: A4 landscape; margin: 8mm; }
    body { font-family: Arial, sans-serif; color: #000; margin: 0; }
    .slip { page-break-after: always; } .slip:last-child { page-break-after: auto; }
    .brand { font-size: 18px; font-weight: 800; letter-spacing: 2px; text-align: center; }
    /* The document's own name, deliberately unemphasised — the company line
       above it is the masthead; this just says which form you are holding. */
    .doc { font-size: 16px; font-weight: 400; text-align: center; margin-top: 3px; }
    .gap { height: 12px; }
    table { width: 100%; border-collapse: collapse; }
    .hdr .hl { font-size: 11px; font-weight: bold; padding: 5px 6px; white-space: nowrap; width: 1%; text-align: left; }
    .hdr .hv { font-size: 14px; padding: 5px 6px; border-bottom: 1px solid #000; white-space: nowrap; }
    .hdr .sp { width: 2%; }
    /* Row metrics are sized so all 25 lines plus the header and signature
       blocks land on ONE A4 landscape page — a slip that breaks onto a
       second sheet loses half its rows at the dispatch window.
       16px is the measured ceiling at this margin: rendering the fullest
       possible slip at 17px spills onto a second page unless the margin
       drops to 6mm, which is inside some printers' unprintable edge. */
    .items { margin-top: 7px; margin-bottom: 12px; table-layout: fixed; }
    .items th, .items td { border: 1px solid #333; padding: 2px 6px; text-align: center; height: 16px; font-size: 11px; }
    .items th { background: #e5e5e5; font-size: 10px; }
    /* The repair marker rides INSIDE the WIP cell. Smaller than the WIP
       number and on the same line, so it neither widens the column nor
       wraps the row onto a second line — both of which would cost the slip
       its one-page fit. Boxed rather than coloured: these print on a mono
       laser at the dispatch window, where a colour is just grey. */
    .items .rep { font-size: 8px; font-weight: bold; border: 1px solid #000; border-radius: 6px; padding: 0 3px; white-space: nowrap; }
    /* The repair block: red AND bold-boxed, so it still reads on a mono laser. */
    .repl { font-size: 10px; font-weight: bold; color: #c00000; margin: 0 0 3px; letter-spacing: 0.06em; }
    .rept { margin-top: 0; }
    .hdr .reptot { color: #c00000; font-weight: bold; }
    .items tr.repr td { color: #c00000; font-weight: bold; }
    .rept td { color: #c00000; font-weight: bold; }
    .foot { page-break-inside: avoid; }
    .foot .sig { width: 33.33%; text-align: center; padding: 0 20px; vertical-align: bottom; }
    /* Name ON the rule, the way it is signed over on paper; the role reads
       underneath it. RECEIVED BY carries a blank of the same height so the
       three columns keep one baseline. */
    /* padding-top, not min-height: the name has to sit ON the rule, and a
       tall box with the text at its top left the signatory floating well
       above the line it belongs to. */
    .foot .nm { font-size: 11px; border-bottom: 1px solid #000; padding-top: 14px; padding-bottom: 1px; }
    .foot .sl { font-size: 9px; font-weight: bold; margin-top: 3px; }
  </style></head><body>${pages}<script>window.onload=function(){window.print();};</script></body></html>`;
}
